import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowUpRight,
  FiBarChart2,
  FiCalendar,
  FiCheck,
  FiCloud,
  FiCreditCard,
  FiLock,
  FiShield,
  FiUsers,
  FiX,
  FiZap
} from 'react-icons/fi';
import { getWorkspaceUpgradeUrl } from '../../utils/billingUpsell.js';

const DEFAULT_COPY = {
  eyebrow: 'Plan upgrade',
  title: 'Unlock this capability',
  description: 'Upgrade to keep this workflow inside Tasklina with the right tools, limits, and workspace controls.',
  benefits: ['Higher limits', 'Advanced workflows', 'Priority workspace tools'],
  icon: FiLock,
  accent: 'bg-slate-950 text-white dark:bg-white dark:text-slate-950',
  soft: 'bg-slate-50 text-slate-700 ring-slate-200 dark:bg-white/5 dark:text-slate-200 dark:ring-white/10'
};

const contextCopy = {
  trial_expired: {
    eyebrow: 'Trial complete',
    title: 'Your workspace is now on the Free plan',
    description: 'The trial ended, so premium tools are locked until you choose a paid plan.',
    benefits: ['Restore premium tools', 'Keep higher limits', 'Continue advanced workflows'],
    icon: FiCalendar,
    accent: 'bg-rose-600 text-white',
    soft: 'bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-400/10 dark:text-rose-100 dark:ring-rose-400/20'
  },
  timeline_view: {
    eyebrow: 'Timeline planning',
    title: 'Plan work on a real delivery timeline',
    description: 'Upgrade to see schedules, dependencies, and release windows in one protected Gantt-style view.',
    benefits: ['Schedule work by date', 'Spot dependency risk', 'Review delivery windows'],
    icon: FiCalendar,
    accent: 'bg-cyan-600 text-white',
    soft: 'bg-cyan-50 text-cyan-800 ring-cyan-200 dark:bg-cyan-400/10 dark:text-cyan-100 dark:ring-cyan-400/20'
  },
  kanban_view: {
    eyebrow: 'Board workflow',
    title: 'Use board view for active task flow',
    description: 'Upgrade to organize work visually across statuses without exposing premium board data on this plan.',
    benefits: ['Drag task flow', 'Review status quickly', 'Run cleaner standups'],
    icon: FiBarChart2,
    accent: 'bg-teal-600 text-white',
    soft: 'bg-teal-50 text-teal-800 ring-teal-200 dark:bg-teal-400/10 dark:text-teal-100 dark:ring-teal-400/20'
  },
  sticky_notes: {
    eyebrow: 'Notes workspace',
    title: 'Turn quick ideas into organized action',
    description: 'Upgrade to use personal and shared notes for planning, follow-ups, and everyday working memory.',
    benefits: ['Personal notes', 'Shared capture', 'Fast follow-ups'],
    icon: FiCheck,
    accent: 'bg-amber-500 text-white',
    soft: 'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-400/10 dark:text-amber-100 dark:ring-amber-400/20'
  },
  audit_logs: {
    eyebrow: 'Activity history',
    title: 'Review workspace activity with confidence',
    description: 'Upgrade to access activity history for audits, accountability, and operational traceability.',
    benefits: ['Track changes', 'Review actions', 'Support compliance'],
    icon: FiShield,
    accent: 'bg-indigo-600 text-white',
    soft: 'bg-indigo-50 text-indigo-800 ring-indigo-200 dark:bg-indigo-400/10 dark:text-indigo-100 dark:ring-indigo-400/20'
  },
  module_finance: {
    eyebrow: 'Finance module',
    title: 'Run budgets and approvals inside the workspace',
    description: 'Upgrade to manage budget requests, approvals, department spend, and finance visibility.',
    benefits: ['Budget requests', 'Approval workflows', 'Finance reporting'],
    icon: FiCreditCard,
    accent: 'bg-emerald-600 text-white',
    soft: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-100 dark:ring-emerald-400/20'
  },
  max_ai_queries: {
    eyebrow: 'AI allowance',
    title: 'Your AI allowance is fully used',
    description: 'Upgrade to keep using AI planning, drafting, and analysis without waiting for the next reset.',
    benefits: ['More AI requests', 'Planning assistance', 'Faster drafting'],
    icon: FiZap,
    accent: 'bg-violet-600 text-white',
    soft: 'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-400/10 dark:text-violet-100 dark:ring-violet-400/20'
  },
  max_storage_gb: {
    eyebrow: 'Storage allowance',
    title: 'Your workspace storage is full',
    description: 'Upgrade to keep files, project context, and history in Tasklina without manual cleanup.',
    benefits: ['More storage', 'Project history', 'Larger workspace archive'],
    icon: FiCloud,
    accent: 'bg-sky-600 text-white',
    soft: 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-400/10 dark:text-sky-100 dark:ring-sky-400/20'
  },
  max_file_size_mb: {
    eyebrow: 'Upload limit',
    title: 'This file is above your upload limit',
    description: 'Upgrade to upload larger documents and assets directly into the workspace.',
    benefits: ['Larger uploads', 'Fewer file splits', 'Cleaner file sharing'],
    icon: FiCloud,
    accent: 'bg-sky-600 text-white',
    soft: 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-400/10 dark:text-sky-100 dark:ring-sky-400/20'
  },
  org_seat: {
    eyebrow: 'Seat allowance',
    title: 'Add more teammates to this workspace',
    description: 'Upgrade to increase seats and keep collaboration, roles, and ownership in one place.',
    benefits: ['More seats', 'Role control', 'Team scaling'],
    icon: FiUsers,
    accent: 'bg-rose-600 text-white',
    soft: 'bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-400/10 dark:text-rose-100 dark:ring-rose-400/20'
  },
  max_departments: {
    eyebrow: 'Department allowance',
    title: 'Add more departments as the org grows',
    description: 'Upgrade to structure teams, reporting, and ownership across more departments.',
    benefits: ['More departments', 'Clear ownership', 'Better reporting'],
    icon: FiUsers,
    accent: 'bg-rose-600 text-white',
    soft: 'bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-400/10 dark:text-rose-100 dark:ring-rose-400/20'
  }
};

function formatLimitValue(value) {
  if (value === null || value === -1 || value === 'unlimited') return 'Unlimited';
  if (value === undefined || value === '') return null;
  return value;
}

function isFeatureLock(code, limit) {
  return limit === 0 || code === 'FEATURE_UNAVAILABLE' || code === 'PLAN_REQUIRED';
}

function getFallbackMessage({ feature, limit, current, workspace }) {
  const limitText = formatLimitValue(limit);
  const currentText = formatLimitValue(current);

  if (limitText !== null && currentText !== null && Number(limit) > 0) {
    return `${workspace} has reached ${currentText} of ${limitText} ${feature}. Upgrade to continue without interrupting the workflow.`;
  }

  return `Upgrade to add ${feature} to this workspace and keep the workflow moving in one place.`;
}

const UpgradePrompt = ({
  feature = 'this feature',
  limit,
  current,
  used,
  itemId,
  code,
  resetAt,
  message,
  workspace = 'personal',
  workspaceType,
  variant = 'inline',
  onClose,
  className = '',
  upgradeUrl
}) => {
  const navigate = useNavigate();
  const resolvedWorkspace = workspaceType || workspace;
  const workspaceLabel = resolvedWorkspace === 'organization' ? 'This organization' : 'This workspace';
  const resolvedCurrent = current ?? used;
  const copy = contextCopy[itemId] || DEFAULT_COPY;
  const Icon = copy.icon;
  const lockedByFeature = isFeatureLock(code, limit);
  const title = copy.title || (lockedByFeature ? `${feature} is on a higher plan` : `${feature} allowance reached`);
  const displayMessage = lockedByFeature
    ? copy.description
    : (message || copy.description || getFallbackMessage({
      feature,
      limit,
      current: resolvedCurrent,
      workspace: workspaceLabel
    }));
  const resolvedUpgradeUrl = upgradeUrl || getWorkspaceUpgradeUrl(resolvedWorkspace);
  const hasUsage = limit !== undefined && resolvedCurrent !== undefined && Number(limit) > 0;
  const usagePercent = hasUsage
    ? Math.min((Number(resolvedCurrent) / Number(limit)) * 100, 100)
    : 0;

  const handleUpgrade = () => {
    navigate(resolvedUpgradeUrl);
    if (onClose) onClose();
  };

  const card = (
    <section className={`relative overflow-hidden rounded-[26px] border border-white/80 bg-white/95 text-slate-950 shadow-[0_24px_72px_rgba(15,23,42,0.22)] backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/95 dark:text-white ${className}`}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-slate-50 to-transparent dark:from-white/5" />

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
          aria-label="Close upgrade prompt"
        >
          <FiX className="h-5 w-5" />
        </button>
      )}

      <div className="relative p-6 sm:p-7">
        <div className="flex items-start gap-4 pr-8">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-lg shadow-slate-950/15 ${copy.accent}`}>
            <Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${copy.soft}`}>
              <FiLock className="h-3.5 w-3.5" />
              {copy.eyebrow}
            </div>
            <h3 className="max-w-xl text-2xl font-semibold leading-8 text-slate-950 dark:text-white">
              {title}
            </h3>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              {displayMessage}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                Upgrade unlocks
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-950 dark:text-white">
                {feature}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${copy.soft}`}>
              {resolvedWorkspace === 'organization' ? 'Org plan' : 'Personal plan'}
            </span>
          </div>

          {hasUsage && (
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>Current usage</span>
                <span>{resolvedCurrent} / {limit}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                <div
                  className={`h-full rounded-full ${copy.accent}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 grid gap-2 sm:grid-cols-3">
          {copy.benefits.map((benefit) => (
            <div
              key={benefit}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 shadow-sm shadow-slate-950/5 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200"
            >
              <FiCheck className="mb-2 h-4 w-4 text-emerald-500" />
              {benefit}
            </div>
          ))}
        </div>

        {resetAt && (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            This allowance resets on {new Date(resetAt).toLocaleDateString()}.
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleUpgrade}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            Upgrade <FiArrowUpRight className="h-4 w-4" />
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
            >
              Keep browsing
            </button>
          )}
        </div>
      </div>
    </section>
  );

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-md">
        <div className="w-full max-w-xl">
          {card}
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`border-b border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur dark:border-white/10 dark:bg-slate-950/95 ${className}`}>
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${copy.accent}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">{title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{displayMessage}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleUpgrade}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 py-2 text-sm font-semibold text-white dark:bg-white dark:text-slate-950"
          >
            Upgrade <FiArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return card;
};

export default UpgradePrompt;
